import React, { Dispatch, SetStateAction, useEffect, useMemo, useState } from "react";
import Grid2 from "@mui/material/Unstable_Grid2";
import { Button, Typography } from "@mui/material";
import { TableDef, TableWrapper } from "../../framework/tableWrapper";
import { TransportOrderInvoicingState, TransportOrderListItemDto, TransportOrderState, transportOrderStates } from "../../../models/transport/transportOrder";
import { TransportOrderStateIndicator } from "../components/transportUtils";
import { MuiSwitch } from "../../framework/muiSwitch";
import { RootState, useAppSelector } from "../../../framework/customStore";
import { MultiSelectButton } from "./MultiSelectButton";
import { Base } from "../../../framework/base";
import { useTranslation } from "react-i18next";

export interface InvoicingTransportOrderListProps {
    orderItems: TransportOrderListItemDto[];
    handleToggleState?: () => void;
    showOnlyInvoiceable?: boolean;
    selectedOrders: TransportOrderListItemDto[];
    setSelectedOrders: Dispatch<SetStateAction<TransportOrderListItemDto[]>>;
    page: number;
    setPage: (page: number) => void;
    rowsPerPage: number;
    setRowsPerPage: (rows: number) => void;
    totalCount: number;
}

export const InvoicingTransportOrderList = ({
    orderItems,
    handleToggleState,
    showOnlyInvoiceable,
    selectedOrders,
    setSelectedOrders,
    page,
    setPage,
    rowsPerPage,
    setRowsPerPage,
    totalCount,
}: InvoicingTransportOrderListProps) => {
    const { t } = useTranslation();
    const state = useAppSelector((state: RootState) => state.transportOrders);
    const stateInvoiceable = transportOrderStates[TransportOrderInvoicingState.Invoiceable];
    const [selectedCustomerId, setSelectedCustomerId] = useState<string>();
    const formatDate = (time: string) => Base.timeToDateTimeStr(new Date(time).getTime());

    useEffect(() => {
        if (selectedOrders) {
            setSelectedCustomerId(selectedOrders[0]?.billingCustomerId);
        } else {
            setSelectedCustomerId("");
        }
    }, [selectedOrders]);

    useEffect(() => {
        if (selectedOrders.length > 0 && selectedCustomerId !== state.billingCustomerId && state.billingCustomerId) {
            setSelectedOrders([]);
        }
    }, [state.billingCustomerId]);

    const handleSelectOrderClick = (item: TransportOrderListItemDto) => {
        setSelectedOrders([...selectedOrders, item]);
    };

    const getDeliveryDate = (order: TransportOrderListItemDto) => {
        if (order.state === TransportOrderState.Delivered &&
            order.latestTransportTaskTypeChangedDateTime
        ) {
            return formatDate(order.latestTransportTaskTypeChangedDateTime);
        }
        return "-";
    };

    const handleDeleteItem = (item: TransportOrderListItemDto) => {
        setSelectedOrders(selectedOrders.filter(i => i.id !== item.id));
    };

    const table = useMemo<TableDef<TransportOrderListItemDto>>(() => ({
        columns: [
            {
                label: t("transport.invoicing.state"),
                accessor: "state",
                renderCell: (val: TransportOrderState, row: TransportOrderListItemDto) => {
                    return (
                        <TransportOrderStateIndicator
                            state={val}
                            createdDatetime={row?.createdDateTime}
                            performedDatetime={
                                row?.latestTransportTaskTypeChangedDateTime
                            }
                        />
                    );
                }
            },
            {
                label: t("transport.invoicing.orderNumber"),
                accessor: "orderNumber",
                width: "7%"
            },
            {
                label: t("transport.invoicing.definition"),
                accessor: "name",
            },
            {
                label: t("transport.invoicing.billingCustomer"),
                accessor: "billingCustomerName",
            },
            {
                label: t("transport.invoicing.deliveryTime"),
                disabledSort: true,
                accessor: row => getDeliveryDate(row),
            },
            {
                label: t("transport.invoicing.priceWithVAT"),
                disabledSort: true,
                accessor: row => row.totalSumWithVat.toFixed(2),
            },
            {
                label: t("transport.invoicing.priceWithoutVAT"),
                disabledSort: true,
                accessor: row => row.totalSum.toFixed(2),
            },
            {
                label: "",
                width: "10%",
                renderCell: (_, row) => (
                    <div style={{ display: "flex", justifyContent: "flex-end" }}>
                        {row.invoicingState === TransportOrderInvoicingState.Invoiceable &&
                            <MultiSelectButton
                                row={row}
                                onClick={handleSelectOrderClick}
                                onRemove={handleDeleteItem}
                                disabled={selectedOrders.length > 0 && selectedCustomerId !== row.billingCustomerId}
                                selected={selectedOrders.some(i => i.id === row.id)}
                            />
                        }
                        {row.state !== TransportOrderState.Delivered &&
                            <Button
                                fullWidth
                                disabled
                                size="small"
                                color="primary"
                            > {t("transport.order.notDelivered")}
                            </Button>}
                        {(row.invoicingState === TransportOrderInvoicingState.AddedToInvoice ||
                            row.invoicingState === TransportOrderInvoicingState.InvoicedInternally ||
                            row.invoicingState === TransportOrderInvoicingState.InvoicedExternally) &&
                            <Button
                                fullWidth
                                disabled
                                size="small"
                                color="primary"
                            >
                                {row.invoicingState === TransportOrderInvoicingState.AddedToInvoice as any
                                    ? t("transport.invoicing.onTheInvoice")
                                    : t("transport.invoicing.invoiced")}
                            </Button>
                        }
                    </div>
                ),
            },
        ],
    }), [selectedCustomerId, selectedOrders.length]);

    return (
        <div style={{ padding: "1rem" }}>
            <Grid2 container justifyContent="space-between">
                <Grid2 xs={3}>
                    <Typography variant="h3">{t("transport.orders")}</Typography>
                </Grid2>
                {showOnlyInvoiceable !== undefined &&
                    <Grid2>
                        <MuiSwitch
                            key={stateInvoiceable.value}
                            checked={!showOnlyInvoiceable}
                            onChange={() => handleToggleState?.()}
                            color="primary"
                            label={t("transport.invoicing.showAllTransportOrders")}
                        />
                    </Grid2>
                }
            </Grid2>
            <Grid2>
                <TableWrapper
                    tableDef={table}
                    data={orderItems}
                    page={page}
                    setPage={setPage}
                    rowsPerPage={rowsPerPage}
                    setRowsPerPage={setRowsPerPage}
                    totalCount={totalCount}
                />
            </Grid2>
        </div>
    );
};
